"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelfOrderThemeResolution = exports.SelfOrderThemeResolutionContext = exports.SelfOrderThemeBinding = exports.SelfOrderThemeBindingScope = exports.SELF_ORDER_THEME_BINDING_SCOPES = exports.SelfOrderThemeRegistryEntry = exports.SelfOrderThemeVersionRecord = exports.SelfOrderThemeLifecycle = exports.REGISTERED_SELF_ORDER_THEME_IDS = void 0;
exports.resolveSelfOrderTheme = resolveSelfOrderTheme;
const zod_1 = require("zod");
const self_order_theme_js_1 = require("./self-order-theme.js");
/**
 * The platform-side theme registry and its bindings
 * (self-order spec v2.3 §4.3, §5, §12.6, §18.18–§18.19).
 *
 * The registry is an allowlist. A theme that is not registered cannot be bound,
 * and a binding cannot name a theme id that no registered record claims — that
 * is what stops "theme" becoming a free-text field through which arbitrary
 * presentation reaches a customer-facing terminal.
 *
 * Nothing here owns theme *content*; that is `SelfOrderThemeManifest`, delivered
 * as a signed artifact. This file owns which theme applies where, and what
 * happens when the answer cannot be honoured.
 */
/* -------------------------------------------------------------------------- */
/* Registered themes                                                          */
/* -------------------------------------------------------------------------- */
/**
 * The themes registered for the first release (spec §2.4: additional customer
 * themes are explicitly out of scope).
 *
 * `reform-lite-default` is both the default for unbranded tenants and the safe
 * fallback when no customer theme resolves (decision 11), which is why it can
 * never be archived — see the refinement on `SelfOrderThemeRegistryEntry`.
 */
exports.REGISTERED_SELF_ORDER_THEME_IDS = [
    self_order_theme_js_1.REFORM_LITE_DEFAULT_THEME_ID,
    self_order_theme_js_1.ORLEN_STOP_CAFE_THEME_ID,
];
exports.SelfOrderThemeLifecycle = zod_1.z.enum(['draft', 'approved', 'archived']);
/**
 * One approved, immutable theme version.
 *
 * Immutable is load-bearing for rollback: rolling back selects a different
 * existing version, it never mutates one. If a published version could be edited
 * in place, "roll back to 3.0.0" would mean whatever 3.0.0 happens to contain
 * today, and the thing you tested would not be the thing you shipped.
 */
exports.SelfOrderThemeVersionRecord = zod_1.z
    .object({
    themeId: self_order_theme_js_1.SelfOrderThemeId,
    themeVersion: self_order_theme_js_1.SelfOrderThemeVersion,
    /** The signed ProvisionArtifact carrying this version's manifest and assets. */
    artifactId: zod_1.z.string().min(1),
    manifestSha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    uiPortVersion: zod_1.z.string().regex(/^\d+\.\d+$/),
    lifecycle: exports.SelfOrderThemeLifecycle,
    approvedBy: zod_1.z.string().min(1).nullable().default(null),
    approvedAt: zod_1.z.string().datetime().nullable().default(null),
    publishedAt: zod_1.z.string().datetime(),
})
    .strict()
    .superRefine((record, ctx) => {
    if (record.lifecycle === 'approved' &&
        (record.approvedBy === null || record.approvedAt === null)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['approvedBy'],
            message: 'an approved theme version must record who approved it and when',
        });
    }
});
exports.SelfOrderThemeRegistryEntry = zod_1.z
    .object({
    themeId: self_order_theme_js_1.SelfOrderThemeId,
    displayName: zod_1.z.string().min(1).max(120),
    /** False for `reform-lite-default`, true for every customer composition. */
    isCustomerTheme: zod_1.z.boolean(),
    lifecycle: exports.SelfOrderThemeLifecycle,
    versions: zod_1.z.array(exports.SelfOrderThemeVersionRecord).min(1),
})
    .strict()
    .superRefine((entry, ctx) => {
    if (entry.themeId === self_order_theme_js_1.REFORM_LITE_DEFAULT_THEME_ID) {
        if (entry.isCustomerTheme) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['isCustomerTheme'],
                message: 'reform-lite-default is the platform baseline, not a customer theme',
            });
        }
        if (entry.lifecycle === 'archived') {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['lifecycle'],
                message: 'reform-lite-default is the safe fallback and cannot be archived',
            });
        }
    }
    if (entry.versions.some((v) => v.themeId !== entry.themeId)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['versions'],
            message: 'every version record must belong to this theme',
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Bindings                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Where a binding attaches. Ordered least to most specific — the array index is
 * the precedence rank, so the ordering is data rather than a comment that can
 * drift from the code below it.
 */
exports.SELF_ORDER_THEME_BINDING_SCOPES = [
    'tenant',
    'location',
    'terminal_profile',
    'terminal',
];
exports.SelfOrderThemeBindingScope = zod_1.z.enum(exports.SELF_ORDER_THEME_BINDING_SCOPES);
exports.SelfOrderThemeBinding = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    scope: exports.SelfOrderThemeBindingScope,
    /** Tenant / location / profile / terminal id, according to `scope`. */
    scopeId: zod_1.z.string().min(1),
    themeId: self_order_theme_js_1.SelfOrderThemeId,
    /**
     * Pin to one version, or null to track the theme's newest approved version.
     * Pinning is the safer default for a launch; tracking is convenient and is
     * how an unreviewed version reaches a customer's screen.
     */
    themeVersion: self_order_theme_js_1.SelfOrderThemeVersion.nullable().default(null),
    /**
     * When true, a terminal that cannot resolve and verify this theme must stop
     * ordering rather than fall back to Default (§4.3 rule 7, §18.18).
     *
     * Defaults false, and should stay false unless a contract demands otherwise:
     * true converts a presentation failure into a closed till.
     */
    brandingRequired: zod_1.z.boolean().default(false),
    createdAt: zod_1.z.string().datetime(),
    createdBy: zod_1.z.string().min(1),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Resolution                                                                 */
/* -------------------------------------------------------------------------- */
exports.SelfOrderThemeResolutionContext = zod_1.z
    .object({
    tenantId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1).nullable().default(null),
    terminalProfileId: zod_1.z.string().min(1).nullable().default(null),
    terminalId: zod_1.z.string().min(1).nullable().default(null),
})
    .strict();
exports.SelfOrderThemeResolution = zod_1.z
    .object({
    themeId: self_order_theme_js_1.SelfOrderThemeId,
    themeVersion: self_order_theme_js_1.SelfOrderThemeVersion.nullable(),
    /** Which binding won, or null when nothing matched and Default applied. */
    resolvedFromScope: exports.SelfOrderThemeBindingScope.nullable(),
    resolvedFromBindingId: zod_1.z.string().min(1).nullable(),
    brandingRequired: zod_1.z.boolean(),
})
    .strict();
/**
 * Most specific binding wins: terminal, then terminal profile, then location,
 * then tenant. With nothing bound, `reform-lite-default` applies — an unbranded
 * tenant is a supported state, not an error.
 *
 * Pure and shared on purpose. Back Office preview, the sync projection and the
 * terminal must all answer this question identically; two implementations of a
 * precedence rule is two answers, and the one the customer sees is whichever
 * happens to run last.
 *
 * Ties within a scope are not resolved here — the platform enforces at most one
 * binding per (scope, scopeId) and this function takes the first match, so a
 * duplicate is a data defect to reject on write, not something to arbitrate on
 * read.
 */
function resolveSelfOrderTheme(bindings, context) {
    const scopeIdFor = {
        tenant: context.tenantId,
        location: context.locationId,
        terminal_profile: context.terminalProfileId,
        terminal: context.terminalId,
    };
    // Walk most specific first.
    for (const scope of [...exports.SELF_ORDER_THEME_BINDING_SCOPES].reverse()) {
        const scopeId = scopeIdFor[scope];
        if (scopeId === null)
            continue;
        const match = bindings.find((b) => b.scope === scope && b.scopeId === scopeId);
        if (match !== undefined) {
            return {
                themeId: match.themeId,
                themeVersion: match.themeVersion,
                resolvedFromScope: scope,
                resolvedFromBindingId: match.id,
                brandingRequired: match.brandingRequired,
            };
        }
    }
    return {
        themeId: self_order_theme_js_1.REFORM_LITE_DEFAULT_THEME_ID,
        themeVersion: null,
        resolvedFromScope: null,
        resolvedFromBindingId: null,
        brandingRequired: false,
    };
}
//# sourceMappingURL=self-order-theme-registry.js.map